import { useAppData } from '@app/providers/app-data-context'
import {
  Box,
  Button,
  FlexBox,
  Link,
  ModalDialog,
  ModalDialogBody,
  ModalDialogButtons,
  ModalDialogContent,
  ModalDialogFooter,
  ModalDialogTitle,
  useScreenClass,
} from '@vp/swan'
import { useIdentity, useStyles } from '@vp/ubik-context'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageFlow } from '../../context/page-flow-context'
import { PageFooter } from './page-footer'
import layoutStyles, { stylesheet } from './styles.scss'

interface PageLayoutProps {
  children: ReactNode
}

export const PageLayout = ({ children }: PageLayoutProps) => {
  useStyles(stylesheet)
  const { t } = useTranslation()
  const { helpCenterUrls } = useAppData()
  const { auth, identity } = useIdentity()
  const { showFooter, isConfirmationStep } = usePageFlow()
  const screenClass = useScreenClass()
  const isIdentitySyncing = Boolean(auth?.getIdentity().isSignedIn) !== Boolean(identity?.isSignedIn)
  const [isExitModalOpen, setIsExitModalOpen] = useState(false)

  const isMobile = screenClass === 'xs'
  const helpCenterPath = helpCenterUrls?.path || '/customer-care/help-center'

  const handleExitClick = () => {
    if (isConfirmationStep) {
      window.location.href = helpCenterPath
      return
    }
    setIsExitModalOpen(true)
  }

  const handleModalDismiss = () => {
    setIsExitModalOpen(false)
  }

  return (
    <FlexBox flexDirection="column" style={{ minHeight: 'calc(100dvh - 245px)' }}>
      <Button className={layoutStyles.exitButton} onClick={handleExitClick}>
        {t('contact_us.page.actions.exit')}
      </Button>
      <ModalDialog isOpen={isExitModalOpen} onRequestDismiss={handleModalDismiss}>
        <ModalDialogContent className={layoutStyles.exitModal}>
          <ModalDialogBody pb={5}>
            <ModalDialogTitle textAlign="center">{t('contact_us.page.actions.exit_modal.title')}</ModalDialogTitle>
          </ModalDialogBody>
          <ModalDialogFooter>
            <ModalDialogButtons className={layoutStyles.exitModalButtons}>
              <Button skin="secondary" width={isMobile ? 'full-width' : 'standard'} onClick={handleModalDismiss}>
                {t('contact_us.page.actions.exit_modal.cancel')}
              </Button>
              <Button
                skin="primary"
                width={isMobile ? 'full-width' : 'standard'}
                render={(props) => (
                  <Link to={helpCenterPath} className={props.className}>
                    {props.children}
                  </Link>
                )}
              >
                {t('contact_us.page.actions.exit_modal.confirm')}
              </Button>
            </ModalDialogButtons>
          </ModalDialogFooter>
        </ModalDialogContent>
      </ModalDialog>
      <Box style={{ flex: 1 }}>{children}</Box>
      {showFooter && !isIdentitySyncing && <PageFooter />}
    </FlexBox>
  )
}
